import { Modal } from "../../UI/Modal/Modal.tsx";
import { Dialog } from "@headlessui/react";
import { OutlinedButton } from "@normasteaching/norma-ui";
import { useMessageController } from "../../UI/useMessageController.ts";
import { SuccessMessage } from "../../UI/Success/SuccessMessage.tsx";
import copy from "copy-to-clipboard";
import { KnownProduct } from "../Product/KnownProduct.tsx";
import { NormasTeachingProductDTO } from "../../ProductService/NormasTeachingProduct/useNormasTeachingProductById.ts";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { ExpandableCard } from "../../UI/ExpandableCard.tsx";

function getProductClassName(productClass: KnownProduct): string {
  const productNames: Record<KnownProduct, string> = {
    [KnownProduct.FPS]: "FPS",
    [KnownProduct.Lil]: "Lil",
    [KnownProduct.Bye]: "Bye",
    [KnownProduct.CvWow]: "CvWow",
    [KnownProduct.Yay]: "Yay",
    [KnownProduct.Akidemy]: "Akidemy",
  };
  return productNames[productClass] || productClass;
}

function ProductDetailsModal({
  product,
  onClose,
}: {
  product: NormasTeachingProductDTO;
  onClose: () => void;
}) {
  const { showMessage, message, open } = useMessageController();

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Dettagli Prodotto"
      overridePanel={({ title, children }) => (
        <Dialog.Panel className="flex-1 md:flex-auto md:max-w-4xl transform overflow-hidden md:rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <Dialog.Title
            as="h3"
            className="text-lg font-medium leading-6 text-gray-900"
          >
            {title}
          </Dialog.Title>
          <div className="mt-4">{children}</div>
        </Dialog.Panel>
      )}
    >
      <SuccessMessage open={open} message={message} />
      <div className="space-y-6 max-h-[70vh] overflow-y-auto max-w-4xl">
        {/* Informazioni generali */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
            Informazioni Generali
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="font-medium text-gray-700">Product ID:</span>
              <p
                className="text-gray-600 cursor-pointer hover:text-blue-600"
                onClick={() => {
                  copy(product._id);
                  showMessage({
                    message: "ID del prodotto copiato negli appunti!",
                    timeoutMs: 4000,
                  });
                }}
              >
                {product._id}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">
                Classe Prodotto:
              </span>
              <p className="text-gray-600">
                {getProductClassName(product.productClass)}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Nome Prodotto:</span>
              <p className="text-gray-600">{product.productName}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Nome Variante:</span>
              <p className="text-gray-600">{product.variantProductName}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Prezzo:</span>
              <p className="text-gray-600">
                €{(product.priceInCents.amount / 100).toFixed(2)}{" "}
                {product.priceInCents.currency.toUpperCase()}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Numero Persone:</span>
              <p className="text-gray-600">{product.peopleCount}</p>
            </div>
            {product.analyticsTag && (
              <div className="col-span-2">
                <span className="font-medium text-gray-700">
                  Analytics Tag:
                </span>
                <p className="text-gray-600">{product.analyticsTag}</p>
              </div>
            )}
          </div>
        </div>

        {/* Descrizione Prodotto */}
        {product.productDescription &&
          product.productDescription.trim() !== "" && (
            <ExpandableCard title="Descrizione Prodotto">
              <div className="prose prose-sm max-w-none mb-4 [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6">
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                  {product.productDescription}
                </ReactMarkdown>
              </div>
            </ExpandableCard>
          )}

        {/* Active Campaign Tags */}
        <ExpandableCard title="Active Campaign Tags">
          <div>
            <span className="font-medium text-gray-700">
              Tag post acquisto:
            </span>
            {product.purchaseAcTags && product.purchaseAcTags.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-2">
                {product.purchaseAcTags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 mt-1">Nessun tag configurato</p>
            )}
          </div>
          <div>
            <span className="font-medium text-gray-700">
              Tag post rimborso:
            </span>
            {product.refundAcTags && product.refundAcTags.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-2">
                {product.refundAcTags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 mt-1">Nessun tag configurato</p>
            )}
          </div>
        </ExpandableCard>

        {/* Prodotti nel Bundle */}
        {product.bundledProducts && product.bundledProducts.length > 0 && (
          <ExpandableCard title="Prodotti inclusi">
            {product.bundledProducts.map((bundledProduct, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-md space-y-1">
                <div>
                  <span className="text-sm text-gray-700">Nome:</span>{" "}
                  {bundledProduct.productName}
                </div>
                <div>
                  <span className="text-sm text-gray-700">ID:</span>{" "}
                  <span
                    className="cursor-pointer hover:text-blue-600"
                    onClick={() => {
                      copy(bundledProduct._id);
                      showMessage({
                        message: "ID del prodotto copiato negli appunti!",
                        timeoutMs: 4000,
                      });
                    }}
                  >
                    {bundledProduct._id}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-700">Classe:</span>{" "}
                  {getProductClassName(bundledProduct.productClass)}
                </div>
              </div>
            ))}
          </ExpandableCard>
        )}

        {/* Configurazione Stripe */}
        {product.stripeConfig && (
          <ExpandableCard title="Configurazione Stripe">
            <div>
              <span className="font-medium text-gray-700">Rate abilitate:</span>
              <p className="text-gray-600">
                {product.stripeConfig.installmentsEnabled ? "Sì" : "No"}
              </p>
            </div>
            {product.stripeConfig.allowedTerms &&
              product.stripeConfig.allowedTerms.length > 0 && (
                <div>
                  <span className="font-medium text-gray-700">
                    Numero rate consentite:
                  </span>
                  <p className="text-gray-600">
                    {product.stripeConfig.allowedTerms.join(", ")}
                  </p>
                </div>
              )}
            {product.stripeConfig.succeededUrl && (
              <div>
                <span className="font-medium text-gray-700">URL successo:</span>
                <p className="text-gray-600 break-all">
                  {product.stripeConfig.succeededUrl}
                </p>
              </div>
            )}
            {product.stripeConfig.failedUrl && (
              <div>
                <span className="font-medium text-gray-700">
                  URL fallimento:
                </span>
                <p className="text-gray-600 break-all">
                  {product.stripeConfig.failedUrl}
                </p>
              </div>
            )}
          </ExpandableCard>
        )}

        {/* Configurazione HeyLight */}
        {product.heylightConfig && (
          <ExpandableCard title="Configurazione HeyLight">
            {product.heylightConfig.allowedTerms &&
              product.heylightConfig.allowedTerms.length > 0 && (
                <div>
                  <span className="font-medium text-gray-700">
                    Numero rate consentite:
                  </span>
                  <p className="text-gray-600">
                    {product.heylightConfig.allowedTerms.join(", ")}
                  </p>
                </div>
              )}
            {product.heylightConfig.successUrl && (
              <div>
                <span className="font-medium text-gray-700">URL successo:</span>
                <p className="text-gray-600 break-all">
                  {product.heylightConfig.successUrl}
                </p>
              </div>
            )}
            {product.heylightConfig.failureUrl && (
              <div>
                <span className="font-medium text-gray-700">
                  URL fallimento:
                </span>
                <p className="text-gray-600 break-all">
                  {product.heylightConfig.failureUrl}
                </p>
              </div>
            )}
          </ExpandableCard>
        )}

        {/* Informazioni FPS */}
        {product.fpsProductInfo && (
          <ExpandableCard title="Informazioni FPS">
            {product.fpsProductInfo.oneOnOne ? (
              <>
                <div>
                  <span className="font-medium text-gray-700">
                    Tipo prodotto:
                  </span>
                  <p className="text-gray-600 font-semibold">1 on 1</p>
                </div>
                {product.fpsProductInfo.oneOnOneTotalLessons !== undefined && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Totale lezioni:
                    </span>
                    <p className="text-gray-600">
                      {product.fpsProductInfo.oneOnOneTotalLessons}
                    </p>
                  </div>
                )}
                {product.fpsProductInfo.oneOnOneDurationInWeeks !==
                  undefined && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Durata piano (settimane):
                    </span>
                    <p className="text-gray-600">
                      {product.fpsProductInfo.oneOnOneDurationInWeeks}
                    </p>
                  </div>
                )}
                {product.fpsProductInfo.oneOnOneMaxCancellations !==
                  undefined && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Cancellazioni massime consentite:
                    </span>
                    <p className="text-gray-600">
                      {product.fpsProductInfo.oneOnOneMaxCancellations}
                    </p>
                  </div>
                )}
                {product.fpsProductInfo.oneOnOneLessonType && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Tipo di lezione:
                    </span>
                    <p className="text-gray-600">
                      {product.fpsProductInfo.oneOnOneLessonType}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {product.fpsProductInfo.durationInWeeks && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Durata (settimane):
                    </span>
                    <p className="text-gray-600">
                      {product.fpsProductInfo.durationInWeeks}
                    </p>
                  </div>
                )}
                {product.fpsProductInfo.lessonsPerWeek && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Lezioni per settimana:
                    </span>
                    <p className="text-gray-600">
                      {product.fpsProductInfo.lessonsPerWeek}
                    </p>
                  </div>
                )}
                {product.fpsProductInfo.unlimitedLessonsPerWeek !==
                  undefined && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Lezioni illimitate:
                    </span>
                    <p className="text-gray-600">
                      {product.fpsProductInfo.unlimitedLessonsPerWeek
                        ? "Sì"
                        : "No"}
                    </p>
                  </div>
                )}
                {product.fpsProductInfo.totalLessons && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Totale lezioni:
                    </span>
                    <p className="text-gray-600">
                      {product.fpsProductInfo.totalLessons}
                    </p>
                  </div>
                )}
                {product.fpsProductInfo.extraWeek !== undefined && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Settimana extra:
                    </span>
                    <p className="text-gray-600">
                      {product.fpsProductInfo.extraWeek ? "Sì" : "No"}
                    </p>
                  </div>
                )}
                {product.fpsProductInfo.warrantyEnabled !== undefined && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Garanzia abilitata:
                    </span>
                    <p className="text-gray-600">
                      {product.fpsProductInfo.warrantyEnabled ? "Sì" : "No"}
                    </p>
                  </div>
                )}
                {product.fpsProductInfo.warrantyMinAttendedLessons && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Lezioni minime per garanzia:
                    </span>
                    <p className="text-gray-600">
                      {product.fpsProductInfo.warrantyMinAttendedLessons}
                    </p>
                  </div>
                )}
                {product.fpsProductInfo.doNotMerge !== undefined && (
                  <div>
                    <span className="font-medium text-gray-700">
                      Non fare il merge:
                    </span>
                    <p className="text-gray-600">
                      {product.fpsProductInfo.doNotMerge ? "Sì" : "No"}
                    </p>
                  </div>
                )}
              </>
            )}
            {product.fpsProductInfo.doNotEnroll !== undefined && (
              <div>
                <span className="font-medium text-gray-700">
                  Non iscrivere:
                </span>
                <p className="text-gray-600">
                  {product.fpsProductInfo.doNotEnroll ? "Sì" : "No"}
                </p>
              </div>
            )}
            {product.fpsProductInfo.userGroupSlug && (
              <div className="col-span-2">
                <span className="font-medium text-gray-700">
                  User Group Slug:
                </span>
                <p className="text-gray-600">
                  {product.fpsProductInfo.userGroupSlug}
                </p>
              </div>
            )}
          </ExpandableCard>
        )}

        {/* Pulsante chiudi */}
        <div className="pt-4 border-t">
          <OutlinedButton onClick={onClose} label="Chiudi" />
        </div>
      </div>
    </Modal>
  );
}

export default ProductDetailsModal;
